import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { Router } from '@angular/router';

import { TableCellDescriptor } from '../model.module/table-cell.model';
import { TableRowAction } from '../model.module/table-row-action.model';
import { SortType } from '../model.module/constant/sort-types.enum';
import { SortFunctions } from '../pipes.module/_sort.functions';

import { AppState } from '../app.state';
import { AppConstants } from '../app-constants.service';
import { Sort } from '../model.module/types/sort.type';

@Component({
    selector: 'elem-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableComponent implements OnChanges, OnInit, OnDestroy {

    searchTerm = '';

    searching = false;

    private readonly entryPkOpenedMap = new Map<string, boolean>();

    private readonly lastSort: Sort = {
        cellName: null,
        sortType: null,
        asc: true
    };

    pk: string = null;

    @Input()
    tableData: any[];

    @Input()
    tableMeta: TableCellDescriptor[];

    @Input()
    tableHover = false;

    @Input()
    targetPath: string;

    @Input()
    targetParam: string;

    @Input()
    searchAction = (foo: string) => {};

    @Input()
    actionFn: Function;

    @Input()
    rowActions: TableRowAction[] = [];

    @Input()
    detailsClass: string;

    @Input()
    detailsTitle: string;

    @Input()
    lineHasChild = false;

    @Input()
    currentOffset = 0;

    @Output()
    goToPageEvent: EventEmitter<number> = new EventEmitter();

    maxShown: number;

    visibleTableData: any[];

    constructor(
        private readonly router: Router,
        private readonly state: AppState,
        private readonly appConstants: AppConstants
    ) {
        this.maxShown = appConstants.pagerDefaultNumPerPage;
    }

    ngOnInit() {
        for (const entry of this.tableMeta) {
            if (entry.pk === true) {
                this.pk = entry.fieldName;
                break;
            }
        }

        if (this.pk !== null) {
            this.tableData.forEach((td) => this.entryPkOpenedMap.set(td[this.pk], false));
        }
    }

    ngOnChanges() {
        if (this.lastSort.cellName !== null) {
            this.sort(true, this.lastSort.cellName, this.lastSort.sortType, false);
        }

        this.paginate();
    }

    private paginate(): void {
        let start = this.currentOffset * this.maxShown;
        let shortened = false;

        while (start > this.tableData.length) {
            start -= this.maxShown;
            shortened = true;
        }

        if (shortened === true) {
            this.goToPageEvent.emit(start / this.maxShown);
        }

        const end = start + this.maxShown;
        this.visibleTableData = this.tableData.slice(start, end);
    }

    ngOnDestroy() {
        this.tableData = [];
    }

    notifySearchKeyup(): void {
        this.searchAction(this.searchTerm);
    }

    visibleRowActions(line: any): TableRowAction[] {
        return this.rowActions.filter((action) => action.isVisible(line));
    }

    executeRowAction(event: Event, action: TableRowAction, line: any): void {
        event.stopPropagation();
        action.execute(line);
    }

    goToTarget = (line: any): void => {
        if (this.actionFn) {
            this.actionFn(line[this.targetParam]);
        } else if (this.targetPath) {
            this.state.storeURL();
            this.router.navigate([this.targetPath], { queryParams: { param: line[this.targetParam] } });
        }
    };

    isSortable(cellName): boolean {
        for (const tcd of this.tableMeta) {
            if (cellName === tcd.fieldName) {
                return tcd.sortable;
            }
        }

        return false;
    }

    ariaSort(cellName: string): 'ascending' | 'descending' | 'none' {
        if (this.lastSort.cellName !== cellName) {
            return 'none';
        }

        return this.lastSort.asc ? 'ascending' : 'descending';
    }

    sort(sortable: boolean, cellName: string, sortType: SortType, switchSort: boolean = true): void {
        if (!sortable) {
            return;
        }

        if (switchSort) {
            this.lastSort.asc = !this.lastSort.asc;
        }

        this.lastSort.cellName = cellName;
        this.lastSort.sortType = sortType;
        SortFunctions.sortArray(sortType, this.tableData, cellName, this.lastSort.asc);
        this.paginate();
    }

    isOpen(line: any): boolean {
        return this.entryPkOpenedMap.get(line[this.pk]);
    }

    toggleOpen(line: any) {
        this.entryPkOpenedMap.set(line[this.pk], !this.entryPkOpenedMap.get(line[this.pk]));
    }

    showSearch(): void {
        this.searching = true;
    }

    hideSearch(): void {
        this.searching = false;
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchAction('');
    }

}
